import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, CreditCard, TrendingUp, Gift, Sparkles, ShieldCheck, Flame, MessageSquare, Send, Crown } from "lucide-react";
import { toast } from "sonner";
import { useProfile } from "../context/ProfileContext";
import { useAuth } from "../context/AuthContext";
import { LEVELS, AVATARS } from "../data/curriculum";
import { AGE_BANDS, AGE_BAND_KEYS } from "../data/story";
import api, { formatApiErrorDetail } from "../services/api";

function heatColor(acc) {
  if (acc == null) return "#E2E8F0";
  if (acc >= 0.85) return "#22C55E";
  if (acc >= 0.6) return "#FACC15";
  if (acc > 0) return "#FB923C";
  return "#E2E8F0";
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function ParentGate({ onPass }) {
  const navigate = useNavigate();
  const [a] = useState(() => 2 + Math.floor(Math.random() * 7));
  const [b] = useState(() => 3 + Math.floor(Math.random() * 6));
  const [val, setVal] = useState("");
  const [err, setErr] = useState(false);

  const check = (e) => {
    e.preventDefault();
    if (Number(val) === a + b) onPass();
    else { setErr(true); setVal(""); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-100">
      <motion.form onSubmit={check} initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-sm rounded-3xl bg-white border border-slate-200 shadow-xl p-7 text-center">
        <div className="text-4xl mb-2">🔒</div>
        <h2 className="font-display text-2xl font-bold text-slate-800 mb-1">Parents Only</h2>
        <p className="text-slate-500 font-medium mb-5 text-sm">Solve to continue</p>
        <p className="font-display text-4xl font-black text-slate-800 mb-4">{a} + {b} = ?</p>
        <input data-testid="parent-gate-input" autoFocus inputMode="numeric" value={val} onChange={(e) => { setVal(e.target.value); setErr(false); }} className={`w-full text-center rounded-xl border-2 ${err ? "border-red-400" : "border-slate-200"} outline-none px-4 py-3 text-2xl font-bold mb-3`} />
        {err && <p className="text-red-500 text-sm font-semibold mb-2">Try again</p>}
        <div className="flex gap-2">
          <button type="button" data-testid="gate-back-btn" onClick={() => navigate("/profiles")} className="flex-1 rounded-xl bg-slate-100 text-slate-600 font-bold py-3">Back</button>
          <button data-testid="parent-gate-submit" type="submit" className="flex-1 rounded-xl bg-slate-800 text-white font-bold py-3">Unlock</button>
        </div>
      </motion.form>
    </div>
  );
}

function SubscriptionCard() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(null);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const sub = user?.subscription || {};

  const checkout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        user_id: user.id,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setBusy(null);
    }
  };

  const redeem = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      await api.post("/billing/redeem", { code: code.trim() });
      toast.success("Gift code applied! 🎁");
      setCode("");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code");
    } finally {
      setRedeeming(false);
    }
  };

  const statusLabel = () => {
    if (sub.status === "active") return `Premium (${sub.plan}) — renews ${fmtDate(sub.current_period_end)}`;
    if (sub.status === "gift") return `${sub.plan} — active until ${fmtDate(sub.current_period_end)}`;
    if (sub.status === "trial") return `Free trial — ends ${fmtDate(sub.current_period_end)}`;
    return "No active plan";
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-5">
      <div className="flex items-center gap-2 mb-3">
        <CreditCard className="w-5 h-5 text-indigo-500" />
        <h2 className="font-display text-lg font-bold text-slate-800">Subscription</h2>
      </div>

      <div className={`flex items-center gap-2 rounded-xl px-4 py-3 mb-4 font-semibold ${sub.active ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`} data-testid="subscription-status">
        <ShieldCheck className="w-5 h-5" />
        <span>{statusLabel()}</span>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="font-display font-bold text-slate-700">Solo <span className="text-xs text-slate-400 font-semibold">· 1 reader</span></span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <button data-testid="subscribe-monthly-btn" onClick={() => checkout("syllaberry_monthly")} disabled={busy} className="pop-btn rounded-2xl border-2 border-slate-200 p-3 text-left hover:border-indigo-300 disabled:opacity-60">
            <div className="font-display text-2xl font-black text-slate-900">$9.99<span className="text-sm font-bold text-slate-400">/mo</span></div>
            <div className="text-xs font-bold text-indigo-500">{busy === "syllaberry_monthly" ? "…" : "Monthly · 7-day trial"}</div>
          </button>
          <button data-testid="subscribe-annual-btn" onClick={() => checkout("syllaberry_yearly")} disabled={busy} className="pop-btn rounded-2xl border-2 border-indigo-300 p-3 text-left relative disabled:opacity-60">
            <span className="absolute top-2 right-2 text-[9px] font-black bg-indigo-500 text-white rounded-full px-2 py-0.5">SAVE 34%</span>
            <div className="font-display text-2xl font-black text-slate-900">$79<span className="text-sm font-bold text-slate-400">/yr</span></div>
            <div className="text-xs font-bold text-indigo-500">{busy === "syllaberry_yearly" ? "…" : "Annual · 7-day trial"}</div>
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="font-display font-bold text-slate-700">Family <span className="text-xs text-amber-500 font-semibold">· up to 5 readers</span></span>
          <span className="text-[10px] font-black bg-amber-400 text-white rounded-full px-2 py-0.5">BEST FOR SIBLINGS</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <button data-testid="subscribe-family-monthly-btn" onClick={() => checkout("syllaberry_family_monthly")} disabled={busy} className="pop-btn rounded-2xl border-2 border-amber-200 p-3 text-left hover:border-amber-400 disabled:opacity-60">
            <div className="font-display text-2xl font-black text-slate-900">$29.99<span className="text-sm font-bold text-slate-400">/mo</span></div>
            <div className="text-xs font-bold text-amber-600">{busy === "syllaberry_family_monthly" ? "…" : "Monthly · 5 kids"}</div>
          </button>
          <button data-testid="subscribe-family-annual-btn" onClick={() => checkout("syllaberry_family_yearly")} disabled={busy} className="pop-btn rounded-2xl border-2 border-amber-300 p-3 text-left disabled:opacity-60">
            <div className="font-display text-2xl font-black text-slate-900">$237<span className="text-sm font-bold text-slate-400">/yr</span></div>
            <div className="text-xs font-bold text-amber-600">{busy === "syllaberry_family_yearly" ? "…" : "Annual · 5 kids"}</div>
          </button>
        </div>
      </div>

      {/* Redeem */}
      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Gift className="w-4 h-4 text-pink-500" />
          <span className="font-bold text-slate-700 text-sm">Have a gift code?</span>
        </div>
        <div className="flex gap-2">
          <input data-testid="redeem-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="XXXX-XXXX" className="flex-1 rounded-xl border-2 border-slate-200 px-3 py-2 font-mono font-bold uppercase tracking-wider outline-none focus:border-pink-300" />
          <button data-testid="redeem-code-btn" onClick={redeem} disabled={redeeming} className="pop-btn rounded-xl bg-pink-500 text-white font-bold px-5 py-2 disabled:opacity-60">
            {redeeming ? "…" : "Redeem"}
          </button>
        </div>
      </div>

      <p className="text-[11px] text-slate-400 mt-3 flex items-center gap-1">
        <Sparkles className="w-3 h-3" /> Test mode — use card 4242 4242 4242 4242, any future date & CVC. Billing goes live when you claim your Stripe account.
      </p>
    </section>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function ContactOwner() {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/messages");
      setMessages(data.messages || []);
    } catch { /* ignore */ }
    finally { setLoaded(true); }
  };

  React.useEffect(() => { load(); }, []);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post("/messages", { body: body.trim() });
      setBody("");
      await load();
      toast.success("Message sent!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
    } finally { setSending(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-5" data-testid="contact-owner-section">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare className="w-5 h-5 text-indigo-500" />
        <h2 className="font-display text-lg font-bold text-slate-800">Message Us</h2>
      </div>
      <p className="text-slate-500 text-sm mb-4">Questions or feedback? Send a note and we'll reply right here.</p>

      {loaded && messages.length > 0 && (
        <div className="space-y-2 max-h-56 overflow-y-auto mb-3 bg-slate-50 rounded-xl p-3 border border-slate-200" data-testid="owner-chat-thread">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender === "parent" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === "parent" ? "bg-indigo-500 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
                {m.sender === "owner" && <div className="flex items-center gap-1 text-[10px] font-bold text-amber-500 mb-0.5"><Crown className="w-3 h-3" /> Syllaberry Team</div>}
                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                <div className={`text-[9px] mt-1 ${m.sender === "parent" ? "text-indigo-200" : "text-slate-400"}`}>{fmtTime(m.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          data-testid="owner-message-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type your message…"
          className="flex-1 rounded-xl border-2 border-slate-200 px-3 py-2.5 outline-none focus:border-indigo-300"
        />
        <button data-testid="owner-message-send" onClick={send} disabled={sending} className="pop-btn rounded-xl bg-indigo-500 text-white font-bold px-4 flex items-center gap-1 disabled:opacity-60">
          <Send className="w-4 h-4" /> {sending ? "…" : "Send"}
        </button>
      </div>
    </section>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const { profiles, updateSettings } = useProfile();
  const { user, logout } = useAuth();
  const [limit, setLimit] = useState(() => Number(localStorage.getItem("pq_daily_limit") || 0));

  const setDaily = (mins) => {
    setLimit(mins);
    localStorage.setItem("pq_daily_limit", String(mins));
  };

  return (
    <div className="min-h-screen bg-slate-50 p-5">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button data-testid="back-to-map-btn" onClick={() => navigate("/profiles")} className="flex items-center gap-2 text-slate-600 font-bold">
            <ArrowLeft className="w-5 h-5" /> Back
          </button>
          <h1 className="font-display text-2xl font-bold text-slate-800">Parent Dashboard</h1>
          <button data-testid="parent-logout-btn" onClick={logout} className="text-sm font-bold text-red-500">Sign out</button>
        </div>
        <div className="flex items-center justify-between mb-6">
          <p className="text-slate-500 font-medium text-sm">Signed in as {user?.email}</p>
          {user?.role === "admin" && (
            <button data-testid="admin-panel-btn" onClick={() => navigate("/admin")} className="flex items-center gap-1 text-xs font-bold text-amber-600 bg-amber-50 rounded-full px-3 py-1.5">
              <Crown className="w-4 h-4" /> Owner Dashboard
            </button>
          )}
        </div>

        {/* Progress heatmap */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-indigo-500" />
            <h2 className="font-display text-lg font-bold text-slate-800">Progress Heatmap</h2>
          </div>
          {profiles.length === 0 && <p className="text-slate-400 text-sm">No reader profiles yet.</p>}
          {profiles.map((p) => (
            <div key={p.id} className="mb-5 last:mb-0" data-testid={`heatmap-${p.id}`}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-2xl">{AVATARS[p.avatar]?.emoji}</span>
                <span className="font-bold text-slate-700">{p.name}</span>
                <span className="flex items-center gap-1 text-xs font-bold text-orange-500"><Flame className="w-3.5 h-3.5" /> {p.streak || 0} day streak</span>
                <span className="text-xs font-bold text-pink-500">🏷️ {(p.stickers || []).length} stickers</span>
                <select
                  data-testid={`age-select-${p.id}`}
                  value={p.age_band || "3-4"}
                  onChange={(e) => updateSettings(p.id, e.target.value)}
                  className="text-xs font-bold text-indigo-600 bg-indigo-50 rounded-lg px-2 py-1 border border-indigo-200 outline-none"
                >
                  {AGE_BAND_KEYS.map((k) => (
                    <option key={k} value={k}>{AGE_BANDS[k].label} · {AGE_BANDS[k].focus}</option>
                  ))}
                </select>
                <span className="text-xs font-bold text-amber-500 ml-auto">⭐ {p.total_stars} / {LEVELS.length * 3}</span>
              </div>
              <div className="grid grid-cols-7 gap-2">
                {LEVELS.map((lvl) => {
                  const prog = (p.progress || {})[String(lvl.id)];
                  const acc = prog ? prog.accuracy : null;
                  return (
                    <div key={lvl.id} className="flex flex-col items-center">
                      <div className="w-full rounded-lg flex items-center justify-center text-white font-bold text-lg aspect-square" style={{ background: heatColor(acc) }} title={`${lvl.phase}: ${acc != null ? Math.round(acc * 100) + "%" : "not started"}`}>
                        {lvl.icon}
                      </div>
                      <span className="text-[9px] font-semibold text-slate-400 mt-1 text-center leading-tight">{lvl.phase.split(" ")[0]}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="flex items-center gap-4 mt-4 text-xs font-semibold text-slate-400 flex-wrap">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500" /> Mastered</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-400" /> Practicing</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-400" /> Needs help</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-200" /> Not started</span>
          </div>
        </section>

        <SubscriptionCard />

        <ContactOwner />

        {/* Time controls */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-indigo-500" />
            <h2 className="font-display text-lg font-bold text-slate-800">Daily Time Limit</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {[0, 15, 30, 45, 60].map((m) => (
              <button key={m} data-testid={`limit-${m}`} onClick={() => setDaily(m)} className={`rounded-xl px-4 py-2 font-bold border-2 ${limit === m ? "bg-indigo-500 text-white border-indigo-500" : "bg-white text-slate-600 border-slate-200"}`}>
                {m === 0 ? "No limit" : `${m} min`}
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default function ParentPortal() {
  const [unlocked, setUnlocked] = useState(false);
  if (!unlocked) return <ParentGate onPass={() => setUnlocked(true)} />;
  return <Dashboard />;
}
